/* eslint-disable react-refresh/only-export-components */
import {createContext, type ReactNode, useReducer, useCallback, useContext, useRef} from "react";
import {projectManager} from "../lib/Solar/ProjectManager";
import {ProjectPoller, type PollResult} from "../lib/Solar/ProjectPoller";
import {createProjectAndOptimize} from "../lib/Solar/SolarApiClient";
import type {OptimizationEffort} from "../lib/Solar/SolarApiClient";
import type {SolarProject} from "../types/solar";
import {
	projectContextReducer,
	initialProjectContextState,
} from "./ProjectContext.reducer";
import {createLogger} from "../lib/logger.ts";

const logger = createLogger("ProjectContext");

/**
 * ProjectContext manages the lifecycle of the current active solar project
 * - Automatically loads projects from usedProjects (LocalStorage)
 * - Creates new projects if address not found
 * - Provides error handling and loading states
 */

interface ProjectContextValue {
    // State
    currentProject: SolarProject | null;
    isLoading: boolean;
    error: string | null;

    // Actions
    loadProjectByAddress: (address: AddressWithPrediction, optimizationEffort?: OptimizationEffort) => Promise<SolarProject>;
    pollProjectUntilTerminal: () => Promise<PollResult>;
    isPolling: () => boolean;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

interface ProjectProviderProps {
    children: ReactNode;
}

/**
 * ProjectProvider wraps the application and manages project state
 * - Handles project loading/creation
 * - Manages loading and error states
 * - Integrates with ProjectManager and LocalStorageManager
 */
export const ProjectProvider = ({children}: ProjectProviderProps) => {
    const [state, dispatch] = useReducer(
        projectContextReducer,
        undefined,
        initialProjectContextState
    );
    const pollerRef = useRef<ProjectPoller | null>(null);

    /** Maximum time to wait for project generation: 3 minutes */
    const POLLING_INTERVAL_MS = 5_000;
    const POLLING_MAX_ATTEMPTS = 36; // 36 × 5 s = 3 min

    const pollProjectUntilTerminal = useCallback((): Promise<PollResult> => {
        if (!pollerRef.current) {
            pollerRef.current = new ProjectPoller(
                {
                    step: () => projectManager.pollGenerationStep(),
                    onStart: () => dispatch({ type: "START_POLLING" }),
                    onCompleted: () => dispatch({ type: "POLLING_COMPLETED" }),
                    onError: (message) => {
                        projectManager.markGenerationError();
                        dispatch({ type: "POLLING_ERROR", payload: message });
                    },
                    onTimeout: () => {
                        logger.warn("polling timed out after 3 minutes");
                        projectManager.markGenerationError();
                        dispatch({ type: "POLLING_TIMEOUT" });
                    },
                },
                { intervalMs: POLLING_INTERVAL_MS, maxAttempts: POLLING_MAX_ATTEMPTS }
            );
        }

        return pollerRef.current.start();
    }, []);

    const loadProjectByAddress: (address: AddressWithPrediction, optimizationEffort?: OptimizationEffort) => Promise<SolarProject> = useCallback(
        async (address: AddressWithPrediction, optimizationEffort: OptimizationEffort = 'fast') => {
            try {
                dispatch({ type: "START_LOADING" });

                // Activate ProjectManager with the address and chosen optimization mode
                projectManager.activate(address, optimizationEffort);

                if (projectManager.hasProject()) {
                    const existingProject = projectManager.getProject();
                    logger.debug("Found existing project in localStorage", { existingProject });

                    // Activate ProjectManager with existing project
                    projectManager.setProject(existingProject);

                    if (existingProject.state === "COMPLETED" && !existingProject.data) {
                        // Try to load full data if available
                        try {
                            await projectManager.fetchProjectData();
                        } catch (err) {
                            logger.error("Error loading full project data from localStorage", { error: err });
                            // Continue with slimmed data if full data unavailable
                        }
                    }

                    dispatch({ type: "PROJECT_LOADED", payload: existingProject });
                    return existingProject;
                }
                logger.debug("Project not found, creating new project");

                // Create new project via API
                const fullAddress = address.postalAddress
                    ? `${address.postalAddress.addressLines?.join(", ")}, ${address.postalAddress.postalCode} ${address.postalAddress.locality}`
                    : address.address;

                const newProject = (await createProjectAndOptimize(
                    fullAddress,
                    address.postalAddress?.regionCode || "DE",
                    undefined,
                    optimizationEffort
                )) as SolarProject;

                if (!newProject || !newProject.projectId) {
                    throw new Error("Failed to create project: No projectId received from API");
                }

                // Attach address and optimization mode to project
                newProject.forAddress = address;
                newProject.optimizationEffort = optimizationEffort;

                // Set in ProjectManager (also saves to localStorage)
                projectManager.setProject(newProject);
                dispatch({ type: "PROJECT_CREATED", payload: newProject });
                return newProject;

            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Failed to load or create project";
                logger.error("Error in loadProjectByAddress", { error: err });
                dispatch({ type: "LOADING_ERROR", payload: errorMessage });
                throw err; // Re-throw for component-level handling if needed
            }
        },
        []
    );

    const isPolling = useCallback(() => state.isPolling, [state.isPolling]);

    const value: ProjectContextValue = {
        currentProject: state.currentProject,
        isLoading: state.isLoading,
        error: state.error,
        loadProjectByAddress,
        pollProjectUntilTerminal,
        isPolling
    };

    return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

/**
 * Hook to use ProjectContext
 * Must be used within ProjectProvider
 */
export const useProject = (): ProjectContextValue => {
    const context = useContext(ProjectContext);

    if (context === undefined) {
        throw new Error("useProject must be used within a ProjectProvider");
    }

    return context;
};
